'use client'

import { usePathname } from 'next/navigation'
import { ProductCardConsumerLayoutProvider } from 'product-card/context/ProductCardConsumerLayoutContext'
import type { ResponsiveProductCardSettings } from 'product-card/context/ProductCardConsumerLayoutContext/ProductCardConsumerLayoutContext'
import { ProductCardImagesSizesProvider } from 'product-card/context/ProductCardImagesSizesContext'
import { ProductListProvider } from 'product-list/context/ProductListProvider/ProductListProvider'
import type {
	ProductListConfig,
	ProductListGroup,
	ProductListItem,
	ProductListParams,
	RenderedProductListItem,
} from 'product-list/types/ProductsList'
import { View } from 'product-list/types/View'
import { getGridImagesSizes } from 'product-list/utils/getGridImagesSizes'
import type { Product } from 'product/types'
import { type ReactNode, useLayoutEffect, useMemo } from 'react'

import { ProductListFiltersProvider } from '../../context/ProductListFiltersProvider/ProductListFiltersProvider'
import type { UseFiltersData, UseFiltersSlotItem } from '../../types/Filters'
import { ClientProductCardItem } from '../ClientProductCardItem/ClientProductCardItem'
import type { GridItem } from '../Grid/Grid'
import { GridGroup } from '../GridGroup/GridGroup'
import { Pagination } from '../Pagination/Pagination'
import { Sticky } from '../Sticky/Sticky'

export type ProductListProductCardResponsiveSettings = Record<
	View,
	ResponsiveProductCardSettings
>

export interface ListProps {
	readonly config: ProductListConfig
	readonly initialParams?: ProductListParams
	readonly defaultView: View
	readonly displayFilters: boolean
	readonly displayViewSelector: boolean
	readonly filterResultPathname?: string
	readonly filtersData?: UseFiltersData
	readonly groups: ProductListGroup[]
	readonly name: string
	readonly preLoadedProductsData: Product[]
	readonly responsiveProductCardSettings: ProductListProductCardResponsiveSettings
	readonly serverProducts: RenderedProductListItem[]
	readonly inspirationalPresentation?: ReactNode
	readonly shouldRenderSlideshowAnimation?: boolean
	readonly onUserViewChange?: (view: View) => void
}

export function ProductList({
	config,
	initialParams,
	defaultView,
	displayFilters,
	displayViewSelector,
	filterResultPathname,
	filtersData,
	groups,
	name,
	preLoadedProductsData,
	responsiveProductCardSettings,
	serverProducts,
	inspirationalPresentation,
	shouldRenderSlideshowAnimation,
	onUserViewChange,
}: ListProps) {
	const pathname = usePathname()

	useLayoutEffect(() => {
		/**
		 * If we are on a persisted page, we can take advantage of native scroll
		 * restoration as we have all products initially available
		 */
		window.onpageshow = (event) => {
			if (event.persisted) {
				window.history.scrollRestoration = 'auto'
			}
		}
	}, [])

	const groupedGridItems = useMemo<GridItem[][]>(() => {
		const serverProductMap = new Map<
			ProductListItem['id'],
			RenderedProductListItem
		>(serverProducts.map((product) => [product.id, product]))
		const priceMap = new Map<
			UseFiltersSlotItem['id'],
			UseFiltersSlotItem['price']
		>(filtersData?.catalogItemsData.map((item) => [item.id, item.price]))
		return groups.map((group) =>
			group.items.map((item) => ({
				id: item.id,
				price: priceMap.get(item.id),
				isFeatured: !!item.images.landscape,
				render: serverProductMap.get(item.id)?.render ?? (
					<ClientProductCardItem
						item={item}
						preLoadedProductsData={preLoadedProductsData}
					/>
				),
			}))
		)
	}, [
		groups,
		preLoadedProductsData,
		serverProducts,
		filtersData?.catalogItemsData,
	])

	const initialCount = useMemo(() => {
		if (serverProducts.length) {
			return serverProducts.length
		}
		return Math.min(
			groups.reduce((acc, group) => acc + group.items.length, 0),
			config.clientPerPage
		)
	}, [config.clientPerPage, groups, serverProducts.length])

	const total = useMemo(
		() =>
			groups.reduce(
				(accumulator, group) => accumulator + group.items.length,
				0
			),
		[groups]
	)

	return (
		<ProductListProvider
			defaultView={defaultView}
			name={name}
			initialCount={initialCount}
			onUserViewChange={onUserViewChange}
			shouldRenderSlideshowAnimation={!!shouldRenderSlideshowAnimation}
		>
			{(view) => (
				<ProductListFiltersProvider
					filtersData={filtersData}
					initialParams={initialParams}
					filterResultPathname={filterResultPathname ?? pathname}
				>
					<Sticky
						displayViewSelector={displayViewSelector}
						displayFilters={displayFilters}
					/>
					{view === View.Inspirational && inspirationalPresentation ? (
						inspirationalPresentation
					) : (
						<ProductCardConsumerLayoutProvider
							layoutView={view}
							settings={responsiveProductCardSettings[view]}
						>
							<ProductCardImagesSizesProvider
								defaultImageSizes={getGridImagesSizes(view)}
							>
								<Pagination total={total} perPage={config.clientPerPage}>
									{(count) => (
										<GridGroup
											groupedGridItems={groupedGridItems}
											itemLimit={count}
										/>
									)}
								</Pagination>
							</ProductCardImagesSizesProvider>
						</ProductCardConsumerLayoutProvider>
					)}
				</ProductListFiltersProvider>
			)}
		</ProductListProvider>
	)
}
